import { RecentlyViewed } from "@theme/recently-viewed-products";

/**
 * The PLP v2 recently-viewed band element (ADR-0002). Reads the visitor's
 * view-history store, fetches server-rendered slim cards through the
 * Section Rendering API search route (`id:` query), and injects them
 * newest-first — data is always rendered fresh, never cached payloads.
 *
 * The band ships hidden; a synchronous inline script in the section
 * un-hides it pre-paint when history exists. This element re-hides it if
 * the history's products yield no renderable cards (e.g. every viewed
 * product has since been hidden or unpublished).
 */
class RecentlyViewedProductsComponent extends HTMLElement {
  connectedCallback() {
    this.#loadCards();
  }

  /**
   * Builds the Section Rendering API URL for a set of product IDs,
   * preserving their recency order in the query.
   * @param {string[]} productIds - Product IDs, newest first.
   * @param {string} searchUrl - The shop's search route.
   * @param {string} sectionFile - The section filename to render.
   * @returns {URL}
   */
  static buildSearchUrl(productIds, searchUrl, sectionFile) {
    const url = new URL(searchUrl || "/search", location.origin);

    url.searchParams.set(
      "q",
      productIds.map((id) => `id:${id}`).join(" OR "),
    );
    url.searchParams.set("resources[type]", "product");
    url.searchParams.set("section_id", sectionFile);

    return url;
  }

  async #loadCards() {
    const cap = Number.parseInt(this.dataset.maxProducts ?? "", 10) || 4;
    const productIds = RecentlyViewed.getProducts().slice(0, cap);

    if (productIds.length === 0) return;

    const url = RecentlyViewedProductsComponent.buildSearchUrl(
      productIds,
      this.dataset.searchUrl,
      this.dataset.sectionFile,
    );

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed with status ${response.status}`);

      const markup = await response.text();
      const doc = new DOMParser().parseFromString(markup, "text/html");
      const results = doc.querySelector("[data-recently-viewed-results]");

      // Search results carry no recency order, so reorder by the history;
      // IDs the search couldn't render (hidden/unpublished products)
      // silently drop out here.
      const cards = productIds
        .map((id) => results?.querySelector(`[data-product-id="${id}"]`))
        .filter((card) => card !== null && card !== undefined);

      if (cards.length === 0) {
        this.hidden = true;
        return;
      }

      const row = this.querySelector("[data-recently-viewed-row]");
      if (!row) return;

      row.replaceChildren(...cards);
      this.hidden = false;
    } catch {
      // A failed fetch means no band, never a broken page.
      this.hidden = true;
    }
  }
}

if (!customElements.get("recently-viewed-products-component")) {
  customElements.define(
    "recently-viewed-products-component",
    RecentlyViewedProductsComponent,
  );
}

export { RecentlyViewedProductsComponent };
